import { Slot } from '@radix-ui/react-slot';
import { cva, VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, DetailedHTMLProps, forwardRef } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { focusRings } from './_common/focusRing';

// Define the base styles, variants, and default variants using CVA
export const buttonVariants = cva(
  [
    focusRings.focus.visible,
    'flex items-center justify-center font-semibold whitespace-nowrap',
    '[&_svg]:shrink-0',
    'select-none'
  ],
  {
    variants: {
      variant: {
        headless: '',
        // Solid variants carry a transparent border as wide as the outlined ones',
        // so switching between them never moves the button or its neighbours.
        primary:
          'border border-transparent bg-brand-500 dark:bg-brand-400 not-aria-readonly:not-aria-disabled:hocus:bg-brand-600 dark:not-aria-readonly:not-aria-disabled:hocus:bg-brand-500 text-white aria-disabled:bg-brand-600/50 dark:aria-disabled:bg-brand-500/50 aria-pressed:bg-brand-600 dark:aria-pressed:bg-brand-500',
        secondary: [
          'bg-bg-default text-fg-default aria-disabled:bg-bg-default/50 aria-disabled:text-fg-default/50 aria-pressed:bg-bg-subtle aria-pressed:text-fg-default',
          'border border-border-default not-aria-readonly:not-aria-disabled:hocus:border-bg-inset aria-pressed:border-bg-inset'
        ],
        secondaryMuted: [
          'bg-bg-default text-fg-muted aria-disabled:bg-bg-default/50 aria-disabled:text-fg-default/50 aria-pressed:bg-bg-subtle aria-pressed:text-fg-default',
          'border border-border-default not-aria-readonly:not-aria-disabled:hocus:border-bg-inset aria-pressed:border-bg-inset'
        ],
        primaryAlt:
          'border border-transparent bg-brand-900 dark:bg-brand-400 text-white aria-disabled:bg-brand-900/50 dark:aria-disabled:bg-brand-400/50 not-aria-readonly:not-aria-disabled:hocus:bg-brand-800 dark:not-aria-readonly:not-aria-disabled:hocus:bg-brand-500 aria-pressed:bg-brand-800 dark:aria-pressed:bg-brand-500',
        danger:
          'border border-transparent bg-bg-danger-emphasis not-aria-readonly:not-aria-disabled:hocus:bg-bg-danger-emphasis-2 text-white aria-disabled:bg-bg-danger-emphasis/50 aria-pressed:bg-bg-danger-emphasis-2',
        dangerSubtle: [
          'bg-bg-danger text-fg-danger border border-border-danger not-aria-readonly:not-aria-disabled:hocus:bg-bg-danger/80 not-aria-readonly:not-aria-disabled:hocus:text-fg-danger not-aria-readonly:not-aria-disabled:hocus:border-border-danger-emphasis aria-disabled:bg-bg-danger/50 aria-disabled:text-fg-danger/50 aria-disabled:border-border-danger aria-pressed:bg-bg-danger-2 aria-pressed:border-border-danger-emphasis'
        ],

        success:
          'border border-transparent bg-bg-success-emphasis not-aria-readonly:not-aria-disabled:hocus:bg-bg-success-emphasis-2 text-white aria-disabled:bg-bg-success-emphasis/50 aria-pressed:bg-bg-success-emphasis-2',
        invert:
          'bg-white/50 not-aria-readonly:not-aria-disabled:hocus:bg-white/20 text-white aria-disabled:bg-white/50 aria-disabled:text-white/50 border border-transparent aria-pressed:bg-white/20 aria-pressed:text-white',
        invertGhost:
          'bg-transparent not-aria-readonly:not-aria-disabled:hocus:bg-white/20 text-white aria-disabled:text-white/50 border border-transparent aria-pressed:bg-white/20 aria-pressed:text-white',
        invertAlt:
          'bg-white/50 not-aria-readonly:not-aria-disabled:hocus:bg-white/60 text-fg-default aria-disabled:bg-white/50 aria-disabled:text-fg-default/50 border border-transparent aria-pressed:bg-white/60 aria-pressed:text-fg-default',
        tertiary:
          'not-aria-readonly:not-aria-disabled:hocus:bg-bg-subtle border border-border-default text-fg-default aria-disabled:text-fg-default/50 aria-pressed:bg-bg-subtle/30 aria-pressed:border-charcoal-dark/10',
        tertiarySelected:
          'bg-grey not-aria-readonly:not-aria-disabled:hocus:bg-grey-light border border-grey text-fg-default aria-disabled:text-fg-default/50 aria-pressed:bg-grey-light',
        link: 'border border-transparent text-fg-default underline decoration-transparent not-aria-readonly:not-aria-disabled:hocus:decoration-current transition-all',
        ghost:
          'border border-transparent not-aria-readonly:not-aria-disabled:hocus:border-border-default not-aria-readonly:not-aria-disabled:hocus:bg-bg-subtle/30 text-fg-default aria-disabled:text-fg-default/50 aria-pressed:bg-bg-subtle/30 aria-pressed:border-border-default'
      },
      size: {
        xs: 'text-xs gap-1 leading-tight px-2 h-7 py-3 [:where(&_svg)]:size-3',
        sm: 'text-base gap-2 leading-tight px-3 h-9 py-1.5 [:where(&_svg)]:size-4',
        md: 'text-base gap-2.5 leading-tight h-12 px-4 py-1.5 [:where(&_svg)]:size-4',
        lg: 'text-lg gap-2.5 leading-tight h-15 px-4 py-1.5 [:where(&_svg)]:size-4'
      },
      layout: {
        normal: '',
        icon: ''
      },
      shape: {
        normal: 'rounded',
        rounded: 'rounded-full',
        tab: 'rounded-t',
        link: 'rounded border-0 underline'
      },
      disabled: {
        true: 'not-aria-readonly:cursor-not-allowed',
        false: ''
      }
    },
    compoundVariants: [
      {
        layout: 'icon',
        size: 'xs',
        className: 'size-7 shrink-0 p-0'
      },
      {
        layout: 'icon',
        size: 'sm',
        className: 'size-9 shrink-0 p-0'
      },
      {
        layout: 'icon',
        size: 'md',
        className: 'size-11 shrink-0 p-0'
      },
      {
        layout: 'icon',
        size: 'lg',
        className: 'size-13 shrink-0 p-0'
      }
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      layout: 'normal',
      shape: 'normal'
    }
  }
);

export type ButtonVariant = VariantProps<typeof buttonVariants>['variant'];
export type ButtonSize = VariantProps<typeof buttonVariants>['size'];
export type ButtonLayout = VariantProps<typeof buttonVariants>['layout'];
export type ButtonShape = VariantProps<typeof buttonVariants>['shape'];

export type ButtonProps = {
  visuallyDisabled?: boolean;
  asChild?: boolean;
  onClickEvenWhenDisabled?: (e: React.MouseEvent<HTMLButtonElement>) => void;
} & VariantProps<typeof buttonVariants> &
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      type = 'button',
      size,
      layout,
      shape,
      visuallyDisabled,
      disabled,
      className,
      asChild,
      onClick,
      onClickEvenWhenDisabled,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : 'button';
    return (
      <Component
        type={type}
        className={cn(
          buttonVariants({
            variant,
            size,
            layout,
            shape,
            disabled: disabled || visuallyDisabled,
            className
          })
        )}
        aria-disabled={disabled || visuallyDisabled}
        disabled={disabled}
        {...props}
        ref={ref}
        onClick={e => {
          if (!disabled && !visuallyDisabled) {
            onClick?.(e);
          }
          onClickEvenWhenDisabled?.(e);
        }}
      />
    );
  }
);
